import React, { useState, useEffect } from 'react';
import { payPeriodAPI, payrollSettingsAPI } from '../services/api';
import type { PayPeriod, PayrollSettings } from '../types';

const PayPeriodManagement: React.FC = () => {
  const [settings, setSettings] = useState<PayrollSettings | null>(null);
  const [payPeriods, setPayPeriods] = useState<PayPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [newStartDate, setNewStartDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, periodsRes] = await Promise.all([
        payrollSettingsAPI.get(),
        payPeriodAPI.getAll(),
      ]);
      setSettings(settingsRes.data);
      setPayPeriods(periodsRes.data.results || []);
      setNewStartDate(settingsRes.data.pay_period_start_date || '');
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.error || 'Failed to load payroll settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSetStartDate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await payrollSettingsAPI.update({ pay_period_start_date: newStartDate });
      alert('Pay period start date saved! Pay periods will be automatically generated.');
      fetchData();
    } catch (err: any) {
      console.error('Failed to save settings:', err);
      alert(err.response?.data?.error || 'Failed to save settings');
    }
  };

  const handleGenerateMore = async () => {
    try {
      const response = await payrollSettingsAPI.generatePeriods();
      alert(response.data.message);
      fetchData();
    } catch (err: any) {
      console.error('Failed to generate periods:', err);
      alert(err.response?.data?.error || 'Failed to generate pay periods');
    }
  };

  const handleLock = async (id: number) => {
    try {
      await payPeriodAPI.lock(id);
      fetchData();
    } catch (err: any) {
      console.error('Failed to lock pay period:', err);
      alert(err.response?.data?.error || 'Failed to lock pay period');
    }
  };

  const handleUnlock = async (id: number) => {
    try {
      await payPeriodAPI.unlock(id);
      fetchData();
    } catch (err: any) {
      console.error('Failed to unlock pay period:', err);
      alert(err.response?.data?.error || 'Failed to unlock pay period');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <h2>Pay Period Management</h2>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Settings Section */}
      <div className="card">
        <h2>Pay Period Configuration</h2>
        
        {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}
        
        <div style={{
          padding: '1.5rem',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px',
          marginBottom: '1.5rem',
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0' }}>How Pay Periods Work</h3>
          <p style={{ margin: 0, color: '#666' }}>
            Set the start date of your first pay period below. The system will automatically 
            generate 14-day (biweekly) pay periods from that date forward. All subsequent 
            periods are calculated from this anchor date.
          </p>
        </div>

        <form onSubmit={handleSetStartDate} style={{ marginBottom: '1.5rem' }}>
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Pay Period Start Date
              </label>
              <input
                type="date"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
                required
                style={{ padding: '0.5rem', fontSize: '1rem' }}
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Save & Generate Periods
            </button>
          </div>
          
          {settings?.is_configured && (
            <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
              Current start date: {formatDate(settings.pay_period_start_date!)}
            </p>
          )}
        </form>

        {/* Preview Section */}
        {settings?.next_period_preview && settings.next_period_preview.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '0.75rem' }}>Upcoming Pay Periods Preview</h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '0.75rem',
            }}>
              {settings.next_period_preview.map((preview, idx) => (
                <div 
                  key={idx}
                  style={{
                    padding: '1rem',
                    backgroundColor: preview.is_current ? '#e8f5e9' : '#f5f5f5',
                    borderRadius: '8px',
                    border: preview.is_current ? '2px solid #4caf50' : '1px solid #ddd',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ 
                    fontWeight: 'bold', 
                    color: preview.is_current ? '#2e7d32' : '#333',
                    marginBottom: '0.25rem',
                  }}>
                    {preview.is_current ? '✓ Current Period' : `Period ${idx + 1}`}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    {formatShortDate(preview.start_date)} - {formatShortDate(preview.end_date)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {settings?.is_configured && (
          <div style={{ 
            padding: '1rem', 
            backgroundColor: '#fff3e0', 
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div>
              <strong>Need more pay periods?</strong>
              <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                Generate additional periods for the next 6 months.
              </p>
            </div>
            <button onClick={handleGenerateMore} className="btn btn-secondary">
              Generate More
            </button>
          </div>
        )}
      </div>

      {/* Advanced Section - Existing Pay Periods */}
      <div className="card">
        <div 
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{ 
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0 }}>
            Existing Pay Periods ({payPeriods.length})
          </h2>
          <span style={{ fontSize: '1.5rem', color: '#666' }}>
            {showAdvanced ? '▼' : '▶'}
          </span>
        </div>

        {showAdvanced && (
          <div style={{ marginTop: '1rem' }}>
            {payPeriods.length === 0 ? (
              <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>
                No pay periods have been created yet. Set a start date above to generate periods.
              </p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Timesheets</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payPeriods.map((period) => (
                    <tr 
                      key={period.id}
                      style={{
                        backgroundColor: period.is_current ? '#e8f5e9' : 'inherit',
                      }}
                    >
                      <td>
                        {period.is_current && (
                          <span style={{
                            padding: '0.125rem 0.5rem',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            marginRight: '0.5rem',
                          }}>
                            CURRENT
                          </span>
                        )}
                        {period.name}
                      </td>
                      <td>{formatDate(period.start_date)}</td>
                      <td>{formatDate(period.end_date)}</td>
                      <td>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: period.is_locked ? '#ffebee' : '#e8f5e9',
                          color: period.is_locked ? '#c62828' : '#2e7d32',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                        }}>
                          {period.is_locked ? '🔒 Locked' : '✓ Open'}
                        </span>
                      </td>
                      <td>{period.timesheets_count || 0}</td>
                      <td>
                        {period.is_locked ? (
                          <button
                            onClick={() => handleUnlock(period.id!)}
                            className="btn btn-secondary"
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
                          >
                            Unlock
                          </button>
                        ) : (
                          <button
                            onClick={() => handleLock(period.id!)}
                            className="btn btn-warning"
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
                          >
                            Lock
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PayPeriodManagement;
